import { useEffect } from "react";

// Google Analytics 4 Configuration
// TODO: Reemplazar con el ID real de GA4 de trineoapp.com (formato: G-XXXXXXXXXX)
// ID numérico de la propiedad visto en la imagen: 444903535
const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || "G-BKDJQV5LC9";

// Función para cargar Google Analytics con configuración mejorada
export const initGA = () => {
  console.log("initGA called with:", {
    GA_TRACKING_ID,
    envVar: process.env.REACT_APP_GA_TRACKING_ID,
    NODE_ENV: process.env.NODE_ENV,
  });

  // Verificar si ya está cargado
  if (window.gtag && window.GA_INITIALIZED) {
    console.log("GA already initialized, skipping");
    return;
  }

  // En desarrollo, simular GA sin cargar el script real para evitar errores
  if (process.env.NODE_ENV === "development") {
    console.log("Development mode: Creating mock gtag");
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      console.log("Mock gtag called with:", arguments);
      window.dataLayer.push(arguments);
    };
    window.GA_INITIALIZED = true;
    return;
  }

  console.log("Creating GA script for production...");

  // Crear script de gtag
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;

  // Agregar handlers de error y carga
  script.onerror = (error) => {
    console.warn(
      "Failed to load Google Analytics script (this is normal with ad blockers):",
      error
    );
    console.warn("Script src was:", script.src);
    // Crear mock gtag para evitar errores en el resto del código
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      console.log("Fallback gtag called with:", arguments);
      window.dataLayer.push(arguments);
    };
    window.GA_INITIALIZED = true;
  };

  // Timeout como fallback
  setTimeout(() => {
    if (!window.gtag) {
      console.warn("GA script timeout, creating fallback gtag");
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        console.log("Timeout fallback gtag called with:", arguments);
        window.dataLayer.push(arguments);
      };
      window.GA_INITIALIZED = true;
    }
  }, 5000); // 5 segundos timeout

  document.head.appendChild(script);

  script.onload = () => {
    console.log("GA script loaded successfully");

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };

    // Inicializar GA4 con configuración base
    window.gtag("js", new Date());
    window.gtag("config", GA_TRACKING_ID, {
      page_title: document.title,
      page_location: window.location.href,
      // Configuraciones adicionales para mejor tracking
      send_page_view: true,
      allow_google_signals: true,
      allow_ad_personalization_signals: true,
      cookie_expires: 63072000, // 2 años
      custom_map: {
        custom_parameter_1: "user_type",
        custom_parameter_2: "page_category",
      },
    });

    // Marcar como inicializado
    window.GA_INITIALIZED = true;

    console.log("Google Analytics 4 initialized successfully");
  };

  // Manejar errores de carga
  script.onerror = () => {
    console.error("Failed to load Google Analytics script");
  };
};

// Función mejorada para rastrear eventos con validación
export const trackEvent = (
  action,
  category = "engagement",
  label = "",
  value = 0,
  customParameters = {}
) => {
  if (typeof window.gtag !== "undefined" && GA_TRACKING_ID !== "G-XXXXXXXXXX") {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
      ...customParameters,
    });

    console.log("GA4 Event tracked:", { action, category, label, value });
  } else {
    console.warn("Google Analytics not initialized or using placeholder ID");
  }
};

// Función para rastrear page views manuales
export const trackPageView = (pageTitle = "", pagePath = "") => {
  if (typeof window.gtag !== "undefined" && GA_TRACKING_ID !== "G-XXXXXXXXXX") {
    window.gtag("config", GA_TRACKING_ID, {
      page_title: pageTitle || document.title,
      page_location: pagePath || window.location.href,
    });

    console.log("GA4 Page view tracked:", { pageTitle, pagePath });
  }
};

// Eventos específicos para conversiones y CTAs
export const trackDownloadApp = (source = "unknown", platform = "unknown") => {
  trackEvent("download_app", "conversion", `${source}_${platform}`, 1, {
    conversion_source: source,
    app_platform: platform,
    conversion_value: 10, // Valor estimado de conversión
    conversion_id: "app_download",
  });
};

export const trackWhatsAppClick = (source = "unknown", context = "general") => {
  trackEvent("whatsapp_click", "conversion", `${source}_${context}`, 1, {
    contact_method: "whatsapp",
    lead_source: source,
    contact_context: context,
    conversion_value: 5,
    conversion_id: "whatsapp_contact",
  });
};

export const trackFormSubmission = (
  formType = "contact",
  source = "unknown"
) => {
  trackEvent("form_submit", "conversion", `${formType}_${source}`, 1, {
    form_type: formType,
    lead_source: source,
    conversion_value: 15,
    conversion_id: "form_submission",
  });
};

export const trackPhoneClick = (source = "unknown") => {
  trackEvent("phone_click", "conversion", source, 1, {
    contact_method: "phone",
    lead_source: source,
    conversion_value: 8,
    conversion_id: "phone_contact",
  });
};

export const trackServiceSelection = (
  serviceType = "unknown",
  source = "unknown"
) => {
  trackEvent("service_selection", "engagement", `${serviceType}_${source}`, 1, {
    service_type: serviceType,
    user_intent: "service_inquiry",
    engagement_level: "high",
  });
};

export const trackLocationSearch = (
  location = "unknown",
  source = "unknown"
) => {
  trackEvent("location_search", "engagement", `${location}_${source}`, 1, {
    searched_location: location,
    user_intent: "local_service",
    engagement_level: "medium",
  });
};

export const trackPriceCalculator = (vehicleType = "unknown", distance = 0) => {
  trackEvent("price_calculator_use", "engagement", vehicleType, distance, {
    vehicle_type: vehicleType,
    estimated_distance: distance,
    user_intent: "price_inquiry",
    engagement_level: "high",
  });
};

export const trackFAQInteraction = (
  question = "unknown",
  source = "faq_page"
) => {
  trackEvent("faq_interaction", "engagement", question, 1, {
    question_topic: question,
    help_seeking: true,
    engagement_level: "medium",
  });
};

export const trackSocialShare = (platform = "unknown", content = "unknown") => {
  trackEvent("social_share", "engagement", `${platform}_${content}`, 1, {
    social_platform: platform,
    shared_content: content,
    viral_action: true,
  });
};

export const trackVideoPlay = (videoId = "unknown", source = "unknown") => {
  trackEvent("video_play", "engagement", `${videoId}_${source}`, 1, {
    video_id: videoId,
    media_type: "video",
    engagement_level: "high",
  });
};

export const trackHeroEngagement = () => {
  trackEvent("hero_interaction", "engagement", "phone_3d", 1, {
    interaction_type: "visual_engagement",
    page_section: "hero",
    engagement_level: "medium",
  });
};

export const trackTestimonialView = (testimonialIndex) => {
  trackEvent(
    "testimonial_view",
    "engagement",
    `testimonial_${testimonialIndex}`,
    1
  );
};

export const trackSectionView = (sectionName) => {
  trackEvent("section_view", "engagement", sectionName, 1);
};

// Función para trackear Core Web Vitals
export const trackCoreWebVitals = ({ name, delta, value, id, rating }) => {
  trackEvent(
    "web_vitals",
    name,
    rating, // good, needs-improvement, poor
    Math.round(value)
  );

  // También enviar como evento custom para análisis detallado
  if (window.gtag) {
    window.gtag("event", "core_web_vitals", {
      custom_parameter_1: name,
      custom_parameter_2: rating,
      value: Math.round(value),
      metric_id: id,
      metric_delta: Math.round(delta),
    });
  }
};

// Hook personalizado para usar Google Analytics en componentes
export const useGoogleAnalytics = () => {
  useEffect(() => {
    initGA();
  }, []);

  return {
    trackEvent,
    trackPageView,
    trackDownloadApp,
    trackWhatsAppClick,
    trackFormSubmission,
    trackPhoneClick,
    trackServiceSelection,
    trackLocationSearch,
    trackPriceCalculator,
    trackFAQInteraction,
    trackSocialShare,
    trackVideoPlay,
    trackHeroEngagement,
    trackCoreWebVitals,
  };
};

// Componente para inicialización automática
const GoogleAnalytics = () => {
  useGoogleAnalytics();
  return null;
};

export default GoogleAnalytics;
